import { Link, useNavigate } from 'react-router-dom';
import { Heart, ShoppingBag } from 'lucide-react';
import { useWishlist } from '../context/WishlistContext';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Star } from 'lucide-react';
import { toast } from 'sonner';

const StarRating = ({ rating, size = 'w-3 h-3' }) => {
  const ratingColor = rating >= 3.5 ? 'text-green-500' : rating >= 2 ? 'text-orange-500' : 'text-red-500';
  return (
    <div className="flex items-center space-x-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} className={`${size} ${i <= Math.round(rating) ? `${ratingColor} fill-current` : 'text-gray-300'}`} />
      ))}
    </div>
  );
};

const ProductCard = ({ product }) => {
  const { isInWishlist, toggleWishlist } = useWishlist();
  const { addToCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const inWishlist = user ? isInWishlist(product.product_id) : false;
  const isOutOfStock = product.stock !== undefined && product.stock <= 0;

  const handleWishlistClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { navigate('/login'); return; }
    toggleWishlist(product.product_id);
  };

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isOutOfStock) return;
    try {
      await addToCart(product.product_id, 1);
      toast.success('Added to bag');
    } catch { toast.error('Failed to add'); }
  };

  return (
    <Link to={`/product/${product.product_id}`} className="group block" data-testid={`product-card-${product.product_id}`}>
      <div className="relative overflow-hidden">
        <img
          src={product.images?.[0] || '/placeholder.jpg'}
          alt={product.name}
          className="w-full aspect-[3/4] object-cover group-hover:scale-105 transition-transform duration-700"
          loading="lazy"
        />
        {/* Hover overlay with Add to Bag — hidden on mobile, shown on hover on desktop */}
        <div className="absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out hidden md:block">
          <button
            onClick={handleAddToCart}
            disabled={isOutOfStock}
            className={`w-full py-3 text-xs uppercase tracking-[0.15em] flex items-center justify-center space-x-2 transition-colors ${
              isOutOfStock
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-forest text-ivory hover:bg-forest/90'
            }`}
            data-testid={`add-to-bag-hover-${product.product_id}`}
          >
            <ShoppingBag className="w-3.5 h-3.5" />
            <span>{isOutOfStock ? 'Out of Stock' : 'Add to Bag'}</span>
          </button>
        </div>
        {/* Always-visible Add to Bag on mobile */}
        <button
          onClick={handleAddToCart}
          disabled={isOutOfStock}
          className={`md:hidden absolute bottom-3 left-3 right-3 py-2.5 text-xs uppercase tracking-[0.1em] flex items-center justify-center space-x-1.5 rounded-sm shadow-lg ${
            isOutOfStock
              ? 'bg-gray-400 text-white cursor-not-allowed'
              : 'bg-forest/95 text-ivory active:bg-forest'
          }`}
          data-testid={`add-to-bag-mobile-${product.product_id}`}
        >
          <ShoppingBag className="w-3.5 h-3.5" />
          <span>{isOutOfStock ? 'Out of Stock' : 'Add to Bag'}</span>
        </button>
        <button
          onClick={handleWishlistClick}
          className="absolute top-3 right-3 p-2.5 bg-white shadow-md rounded-full hover:shadow-lg hover:scale-110 transition-all duration-200"
          data-testid={`wishlist-button-${product.product_id}`}
        >
          <Heart
            className={`w-4 h-4 transition-colors ${inWishlist ? 'fill-red-500 text-red-500' : 'text-forest'}`}
          />
        </button>
        {product.original_price && product.original_price > product.price && !isOutOfStock && (
          <span className="absolute top-3 left-3 bg-green-600 text-white text-[10px] px-2 py-1 uppercase tracking-wider font-medium" data-testid={`discount-badge-${product.product_id}`}>
            {Math.round((1 - product.price / product.original_price) * 100)}% OFF
          </span>
        )}
        {!product.original_price && product.is_featured && (
          <span className="absolute top-3 left-3 bg-gold text-forest text-xs px-3 py-1 uppercase tracking-wider">
            Featured
          </span>
        )}
        {isOutOfStock && (
          <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
            <span className="bg-white/90 text-forest text-xs uppercase tracking-wider px-4 py-2 font-medium">Out of Stock</span>
          </div>
        )}
        {!isOutOfStock && product.stock !== undefined && product.stock <= 5 && (
          <span className="absolute top-3 left-3 bg-red-500 text-white text-[10px] px-2 py-1 uppercase tracking-wider">
            Only {product.stock} left
          </span>
        )}
      </div>
      <div className="pt-4 space-y-1">
        <h3 className="text-forest text-sm font-medium truncate">{product.name}</h3>
        <p className="text-muted-foreground text-xs capitalize">{product.fabric} | {product.color}</p>
        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <p className="text-forest font-heading text-lg">{'\u20B9'}{product.price?.toLocaleString()}</p>
            {product.original_price && product.original_price > product.price && (
              <>
                <p className="text-muted-foreground text-xs line-through">{'\u20B9'}{product.original_price?.toLocaleString()}</p>
                <span className="text-green-600 text-[10px] font-medium">{Math.round((1 - product.price / product.original_price) * 100)}% OFF</span>
              </>
            )}
          </div>
          {product.avg_rating > 0 && (
            <div className="flex items-center space-x-1" data-testid={`product-rating-${product.product_id}`}>
              <StarRating rating={product.avg_rating} />
              <span className="text-xs text-muted-foreground">({product.rating_count || 0})</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

export { StarRating };
export default ProductCard;
